import { v1 as uuid } from 'uuid'

import { cannot } from '@/app/helpers'
import {
  ContentFlow,
  ContentFlowElement,
  ContentFlowPage,
  Message,
  PartFamily,
  TWhatsAppButtons,
  TWhatsAppList,
} from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { KeysOfUnion } from '@/app/types'

import { ActionsData, WithActionsObject } from './types'

export const createCompactId = (): string => uuid().replace(/-/g, '')

export const generateUniqueIdActions = (actions?: Action[] | null): Action[] =>
  (actions || []).map((action) => ({
    ...action,
    id: uuid(),
  }))

export const generateUniqueIdActionsObject = <T extends WithActionsObject>(obj: T | undefined): T | undefined => {
  if (!obj) {
    return undefined
  }

  return {
    ...obj,
    actions: generateUniqueIdActions(obj.actions),
  }
}

const generateUniqueIdRequiredActionsObject = <T extends WithActionsObject>(obj: T): T => ({
  ...obj,
  actions: generateUniqueIdActions(obj.actions),
})

export const generateUniqueIdActionsArray = <T extends WithActionsObject>(array: T[]): T[] => {
  return array.map(generateUniqueIdActionsObject).filter(Boolean) as T[]
}

const generateUniqueIdWhatsappButtons = (buttons: TWhatsAppButtons): TWhatsAppButtons => {
  return {
    ...buttons,
    buttons: buttons.buttons.map((button) => ({
      ...button,
      id: uuid(),
      actions: generateUniqueIdActions(button.actions),
    })),
  }
}

const generateUniqueIdWhatsappList = (list: TWhatsAppList): TWhatsAppList => {
  return {
    ...list,
    items: list.items.map((item) => ({
      ...item,
      id: uuid(),
      actions: generateUniqueIdActions(item.actions),
    })),
  }
}

type ContentFlowType = Exclude<keyof ContentFlowElement, 'metasid' | 'questionId'>
type WaFlowQuestionIdMap = Record<string, string>
type Translations = Record<string, Record<string, unknown>>

const remapWaFlowQuestionIds = (value: string, questionIdMap: WaFlowQuestionIdMap): string =>
  value.replace(/sub\.answer\.id\.([^.{}]+)\./g, (match, questionId: string) => {
    const nextQuestionId = questionIdMap[questionId]

    return nextQuestionId ? `sub.answer.id.${nextQuestionId}.` : match
  })

const remapWaFlowQuestionIdField = (value: unknown, questionIdMap: WaFlowQuestionIdMap): unknown =>
  typeof value === 'string' ? remapWaFlowQuestionIds(value, questionIdMap) : value

const remapWaFlowQuestionIdFields = <
  T extends Record<string, unknown>,
  K extends readonly KeysOfUnion<T>[],
  S extends K[number],
>(
  value: T,
  fields: S[],
  questionIdMap: WaFlowQuestionIdMap,
): T => {
  return fields.reduce(
    (nextValue, field) => {
      if (!(field in nextValue)) {
        return nextValue
      }

      return {
        ...nextValue,
        [field]: remapWaFlowQuestionIdField(nextValue[field], questionIdMap),
      }
    },
    { ...value },
  )
}

const remapWaFlowTranslations = <T extends Translations, K extends readonly KeysOfUnion<T>[], S extends K[number]>(
  translations: T,
  fields: S[],
  questionIdMap: WaFlowQuestionIdMap,
): T => {
  return Object.fromEntries(
    Object.entries(translations).map(([language, translation]) => [
      language,
      remapWaFlowQuestionIdFields(translation, fields, questionIdMap),
    ]),
  ) as T
}

const remapWaFlowTextContent = <
  T extends Record<string, unknown> & { translations?: Translations | null },
  K extends readonly KeysOfUnion<T>[], // all union keys of type T
  S extends K[number], // a single key of T to avoid having to define all keys of T in the fields array
>(
  content: T,
  fields: S[],
  questionIdMap: WaFlowQuestionIdMap,
): T => {
  const contentWithUpdatedFields = remapWaFlowQuestionIdFields(content, fields, questionIdMap)
  if (!content.translations) {
    return contentWithUpdatedFields
  }

  return {
    ...contentWithUpdatedFields,
    translations: remapWaFlowTranslations(content.translations, fields, questionIdMap),
  }
}

const remapWaFlowHeaders = <T extends { key: string; value: string }[]>(
  headers: T,
  questionIdMap: WaFlowQuestionIdMap,
): T => {
  return headers.map((header) => ({
    ...header,
    key: remapWaFlowQuestionIds(header.key, questionIdMap),
    value: remapWaFlowQuestionIds(header.value, questionIdMap),
  })) as T
}

const remapWaFlowAction = (action: Action, questionIdMap: WaFlowQuestionIdMap): Action => {
  switch (true) {
    case 'makeAPIRequest' in action:
      return {
        ...action,
        makeAPIRequest: {
          ...action.makeAPIRequest,
          headers: remapWaFlowHeaders(action.makeAPIRequest.headers, questionIdMap),
          payload: remapWaFlowQuestionIds(action.makeAPIRequest.payload, questionIdMap),
          url: remapWaFlowQuestionIds(action.makeAPIRequest.url, questionIdMap),
        },
      }

    case 'play' in action:
      return {
        ...action,
        play: remapWaFlowTextContent(action.play, ['playfile', 'say', 'voice'], questionIdMap),
      }

    case 'sendEmail' in action:
      return {
        ...action,
        sendEmail: {
          ...action.sendEmail,
          from: remapWaFlowQuestionIds(action.sendEmail.from, questionIdMap),
          message: remapWaFlowQuestionIds(action.sendEmail.message, questionIdMap),
          subject: remapWaFlowQuestionIds(action.sendEmail.subject, questionIdMap),
          to: action.sendEmail.to ? remapWaFlowQuestionIds(action.sendEmail.to, questionIdMap) : action.sendEmail.to,
          toMultiple: action.sendEmail.toMultiple.map((to) => remapWaFlowQuestionIds(to, questionIdMap)),
        },
      }

    case 'sendSMS' in action:
      return {
        ...action,
        sendSMS: remapWaFlowTextContent(action.sendSMS, ['text'], questionIdMap),
      }

    case 'sendSMSToPhoneNumbers' in action:
      return {
        ...action,
        sendSMSToPhoneNumbers: {
          ...remapWaFlowTextContent(action.sendSMSToPhoneNumbers, ['text'], questionIdMap),
          phoneNumbers: action.sendSMSToPhoneNumbers.phoneNumbers.map((phoneNumber) =>
            remapWaFlowQuestionIds(phoneNumber, questionIdMap),
          ),
        },
      }

    case 'sendWhatsapp' in action:
      return {
        ...action,
        sendWhatsapp: remapWaFlowTextContent(
          action.sendWhatsapp,
          ['audio', 'document', 'image', 'text', 'video'],
          questionIdMap,
        ),
      }

    case 'topup' in action:
      return {
        ...action,
        topup: remapWaFlowQuestionIdFields(
          action.topup,
          ['desiredAmountTemplated', 'maxAmount', 'maxAmountTemplated'],
          questionIdMap,
        ),
      }

    case 'transfer' in action:
      return {
        ...action,
        transfer: {
          ...action.transfer,
          to: remapWaFlowQuestionIds(action.transfer.to, questionIdMap),
        },
      }

    case 'updateContact' in action:
      return {
        ...action,
        updateContact: remapWaFlowQuestionIdFields(action.updateContact, ['constant', 'value'], questionIdMap),
      }

    case 'finishFlow' in action:
    case 'hangup' in action:
    case 'pickUp' in action:
    case 'jump' in action:
    case 'replayQuestion' in action:
    case 'resendQuestion' in action:
    case 'runQuota' in action:
    case 'subscribeToCampaign' in action:
    case 'unsubscribe' in action:
    case 'unsubscribeAll' in action:
      return action

    default:
      return cannot(action)
  }
}

const generateUniqueIdWaFlowOnAnswer = (
  onAnswer: ContentFlow['onAnswer'],
  questionIdMap: WaFlowQuestionIdMap,
): ContentFlow['onAnswer'] => ({
  ...onAnswer,
  actions: generateUniqueIdActions(onAnswer.actions).map((action) => remapWaFlowAction(action, questionIdMap)),
})

const isWaFlowContentType = (key: ContentFlowType): true => {
  if (key === 'datePicker') {
    return true
  }
  if (key === 'footer') {
    return true
  }
  if (key === 'heading') {
    return true
  }
  if (key === 'longText') {
    return true
  }
  if (key === 'multiSelect') {
    return true
  }
  if (key === 'shortText') {
    return true
  }
  if (key === 'singleSelect') {
    return true
  }
  if (key === 'subheading') {
    return true
  }

  return cannot(key)
}

const hasWaFlowElement = (element: ContentFlowElement): boolean => {
  if (element.datePicker) {
    return isWaFlowContentType('datePicker')
  }
  if (element.footer) {
    return isWaFlowContentType('footer')
  }
  if (element.heading) {
    return isWaFlowContentType('heading')
  }
  if (element.longText) {
    return isWaFlowContentType('longText')
  }
  if (element.multiSelect) {
    return isWaFlowContentType('multiSelect')
  }
  if (element.shortText) {
    return isWaFlowContentType('shortText')
  }
  if (element.singleSelect) {
    return isWaFlowContentType('singleSelect')
  }
  if (element.subheading) {
    return isWaFlowContentType('subheading')
  }

  return false
}

const generateUniqueIdWhatsAppFlowElement = (element: ContentFlowElement): ContentFlowElement => {
  if (!hasWaFlowElement(element)) {
    throw new Error(`cannot happen: ${JSON.stringify(element)}`)
  }

  const elementWithoutMetasid = { ...element }
  delete elementWithoutMetasid.metasid

  const nextElement: ContentFlowElement = {
    ...elementWithoutMetasid,
    questionId: uuid(),
  }

  if (element.multiSelect) {
    nextElement.multiSelect = {
      ...element.multiSelect,
      answers: element.multiSelect.answers.map((answer) => ({
        ...answer,
        id: createCompactId(),
      })),
    }
  }

  if (element.singleSelect) {
    nextElement.singleSelect = {
      ...element.singleSelect,
      answers: element.singleSelect.answers.map((answer) => ({
        ...answer,
        id: createCompactId(),
      })),
    }
  }

  return nextElement
}

const generateUniqueIdWhatsAppFlowPage = (page: ContentFlowPage): ContentFlowPage => {
  const pageWithoutMetasid = { ...page }
  delete pageWithoutMetasid.metasid

  return {
    ...pageWithoutMetasid,
    elements: page.elements.map(generateUniqueIdWhatsAppFlowElement),
    id: uuid(),
  }
}

const getWaFlowQuestionIdMap = (flow: ContentFlow, pages: ContentFlowPage[]): WaFlowQuestionIdMap => {
  return flow.pages.reduce<WaFlowQuestionIdMap>((questionIdMap, page, pageIndex) => {
    page.elements.forEach((element, elementIndex) => {
      const nextQuestionId = pages[pageIndex]?.elements[elementIndex]?.questionId
      if (nextQuestionId) {
        questionIdMap[element.questionId] = nextQuestionId
      }
    })

    return questionIdMap
  }, {})
}

const generateUniqueIdWhatsAppFlow = (flow: ContentFlow): ContentFlow => {
  const pages = flow.pages.map(generateUniqueIdWhatsAppFlowPage)
  const questionIdMap = getWaFlowQuestionIdMap(flow, pages)

  return {
    ...flow,
    id: uuid(),
    // Only the submitted-response editor offers sub.answer.id.* personalizations.
    onAnswer: generateUniqueIdWaFlowOnAnswer(flow.onAnswer, questionIdMap),
    onInvalidAnswer: generateUniqueIdRequiredActionsObject(flow.onInvalidAnswer),
    onRetriesExhausted: generateUniqueIdRequiredActionsObject(flow.onRetriesExhausted),
    onTimeout: generateUniqueIdRequiredActionsObject(flow.onTimeout),
    pages,
  }
}

export const ensureUniqueIdForMessageActions = (message: Message): ActionsData => {
  let actionsData: ActionsData = {}

  switch (message.type) {
    case PartFamily.Action:
      actionsData = {
        actionsOnly: generateUniqueIdActionsObject(message.actionsOnly),
      }
      break
    case PartFamily.QuotaActions:
      actionsData = {
        quotaActions: {
          onExhausted: generateUniqueIdActions(message.quotaActions.onExhausted),
          onOpen: generateUniqueIdActions(message.quotaActions.onOpen),
        },
      }
      break
    case PartFamily.SMS:
      if (!message.hasQuestion) {
        break
      }
      actionsData = {
        ...actionsData,
        onAnswer: generateUniqueIdActionsArray(message.onAnswer || []),
        onInvalidReply: generateUniqueIdActionsObject(message.onInvalidReply),
        onRetriesExhausted: generateUniqueIdActionsObject(message.onRetriesExhausted),
        onTimeout: generateUniqueIdActionsObject(message.onTimeout),
      }
      if (message.openEnded) {
        actionsData.openEnded = generateUniqueIdActionsObject(message.openEnded)
        if (message.openEnded.categories) {
          actionsData.openEnded!.categories = generateUniqueIdActionsArray(message.openEnded.categories)
        }
      }

      break

    case PartFamily.Voice:
      if (message.callResultActions) {
        actionsData.callResultActions = generateUniqueIdActionsArray(message.callResultActions)
      }
      if (!message.hasQuestion) {
        break
      }
      actionsData = {
        ...actionsData,
        onAnswer: generateUniqueIdActionsArray(message.onAnswer || []),
        onInvalidReply: generateUniqueIdActionsObject(message.onInvalidReply),
        onRetriesExhausted: generateUniqueIdActionsObject(message.onRetriesExhausted),
        onTimeout: generateUniqueIdActionsObject(message.onTimeout),
      }
      if (message.spoken) {
        actionsData.spoken = generateUniqueIdActionsObject(message.spoken)
      }

      break
    case PartFamily.WhatsApp: {
      if (!message.hasQuestion) {
        break
      }

      actionsData = {
        ...actionsData,
        onAnswer: generateUniqueIdActionsArray(message.onAnswer || []),
        onInvalidReply: generateUniqueIdActionsObject(message.onInvalidReply),
        onRetriesExhausted: generateUniqueIdActionsObject(message.onRetriesExhausted),
        onTimeout: generateUniqueIdActionsObject(message.onTimeout),
      }

      if (message.onButtons) {
        actionsData.onButtons = generateUniqueIdWhatsappButtons(message.onButtons)
      }

      if (message.onList) {
        actionsData.onList = generateUniqueIdWhatsappList(message.onList)
      }

      if (message.openEnded) {
        actionsData.openEnded = generateUniqueIdActionsObject(message.openEnded)
        if (message.openEnded.categories) {
          actionsData.openEnded!.categories = generateUniqueIdActionsArray(message.openEnded.categories)
        }
      }

      break
    }
    case PartFamily.WhatsAppFlow:
      actionsData = {
        flow: generateUniqueIdWhatsAppFlow(message.flow),
      }
      break

    case PartFamily.Topup:
      actionsData = {
        topup: {
          ...message.topup,
          onError: generateUniqueIdActions(message.topup?.onError),
          onSuccess: generateUniqueIdActions(message.topup?.onSuccess),
        },
      }
      break
    case PartFamily.API: {
      actionsData = {
        apiCall: {
          ...message.apiCall,
          onError: generateUniqueIdActions(message.apiCall?.onError),
          onSuccess: generateUniqueIdActions(message.apiCall?.onSuccess),
        },
      }
      break
    }
    case PartFamily.CATI: {
      if (!message.hasQuestion) {
        break
      }

      actionsData = {
        ...actionsData,
        onAnswer: generateUniqueIdActionsArray(message.onAnswer || []),
      }
      if (message.openEnded) {
        actionsData.openEnded = generateUniqueIdActionsObject(message.openEnded)
      }
      break
    }
    default:
      return cannot(message)
  }

  return actionsData
}
